import { useEffect, useRef, useState } from 'react';
import { api, track } from '../api';

interface SponsorMedia {
  kind: 'image' | 'video';
  url: string;
  poster_url: string;
  width: number | null;
  height: number | null;
}

type Slot = 'top' | 'second';

interface Sponsor {
  id: string;
  slot?: Slot;
  name: string;
  headline: string;
  body: string | null;
  click_url: string;
  media: SponsorMedia | null;
}

/** Autoplay is skipped for people who asked their device to reduce motion or save data: they get
 * the still frame instead, and the ad still works. */
function shouldAutoplay(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return !conn?.saveData;
}

/** A looping, muted video that only plays while it is on screen. The whole picture is a tracked
 * link to the advertiser; the sound toggle sits outside the link so tapping it doesn't navigate. */
function AdVideo({ media, href }: { media: SponsorMedia; href: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [autoplay] = useState(shouldAutoplay);

  useEffect(() => {
    const v = ref.current;
    if (!v || !autoplay || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.5 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [autoplay]);

  useEffect(() => {
    if (ref.current) ref.current.muted = muted;
  }, [muted]);

  return (
    <div className="sponsor-media" style={media.width && media.height ? { aspectRatio: `${media.width} / ${media.height}` } : undefined}>
      <a href={href} target="_blank" rel="sponsored noopener" tabIndex={-1} aria-hidden="true">
        {autoplay ? (
          <video ref={ref} src={media.url} poster={media.poster_url} muted loop playsInline preload="none" />
        ) : (
          <img src={media.poster_url} alt="" loading="lazy" />
        )}
      </a>
      {autoplay && (
        <button type="button" className="sponsor-sound" aria-pressed={!muted} onClick={() => setMuted((m) => !m)}>
          {muted ? 'Sound off' : 'Sound on'}
        </button>
      )}
    </div>
  );
}

/**
 * Spec §19 "sponsored card between results". Rules from §39, enforced by how this is built:
 * it is its own <li>, never one of the ranked events (rule 1: paying can't move a sale), it
 * always says "Sponsored" (rule 2), and it only ever holds the sponsor's own copy (rule 3).
 *
 * Renders nothing at all -- not even an empty <li> -- when there is no eligible sponsor.
 */
interface Slots {
  top: Sponsor | null;
  second: Sponsor | null;
}

// Which sponsors show depends on where the visitor is (decided server-side), not on what they are
// searching, so both slots come from one request shared by every card on the page, and a pan or a
// new search doesn't re-roll them.
let slotsRequest: Promise<Slots> | null = null;
function loadSlots(): Promise<Slots> {
  slotsRequest ??= api
    .get<{ top?: Sponsor | null; second?: Sponsor | null; sponsor?: Sponsor | null }>('/sponsors/results-card')
    .then((r) => ({ top: r.top ?? null, second: r.second ?? r.sponsor ?? null }))
    .catch(() => ({ top: null, second: null }));
  return slotsRequest;
}

export function SponsorCard({ slot }: { slot: Slot }) {
  const [sponsor, setSponsor] = useState<Sponsor | null>(null);
  const reported = useRef<string | null>(null);

  useEffect(() => {
    let live = true;
    void loadSlots().then((s) => live && setSponsor(s[slot]));
    return () => {
      live = false;
    };
  }, [slot]);

  useEffect(() => {
    if (sponsor && reported.current !== sponsor.id) {
      reported.current = sponsor.id;
      track('sponsor_impression', { sponsor_id: sponsor.id, placement: 'results_card', slot });
    }
  }, [sponsor, slot]);

  if (!sponsor) return null;
  return (
    <li className="results-sponsor" aria-label="Sponsored">
      <span className="sponsor-label">Sponsored</span>
      {sponsor.media?.kind === 'video' && <AdVideo media={sponsor.media} href={sponsor.click_url} />}
      {sponsor.media?.kind === 'image' && (
        <div className="sponsor-media" style={sponsor.media.width && sponsor.media.height ? { aspectRatio: `${sponsor.media.width} / ${sponsor.media.height}` } : undefined}>
          <a href={sponsor.click_url} target="_blank" rel="sponsored noopener" tabIndex={-1} aria-hidden="true">
            <img src={sponsor.media.url} alt="" loading="lazy" />
          </a>
        </div>
      )}
      <a href={sponsor.click_url} target="_blank" rel="sponsored noopener">
        <strong>{sponsor.headline}</strong>
      </a>
      <span className="sponsor-name">{sponsor.name}</span>
      {sponsor.body && <p>{sponsor.body}</p>}
    </li>
  );
}
